import React, { useState, useMemo } from "react";

/**
 * CampImageGallery — displays a single main image with a row of thumbnails
 * for browsing additional photos of the same camp without leaving the page.
 *
 * Props:
 *  - images: string[]        — full gallery (index 0 is the initial main)
 *  - alt: string             — accessible alt text
 *  - cmsScope: string        — data-cms-scope for the admin inline editor
 *  - testIdBase: string      — base for data-testid (e.g. "camp-bivouac-la-belle-etoile")
 *  - overlay: React.ReactNode — optional badge rendered over the main image
 *  - accentClass: string     — tailwind class for the active-thumb border color
 */
const CampImageGallery = ({
  images,
  alt,
  cmsScope,
  testIdBase,
  overlay,
  accentClass = "border-[#C16542]",
}) => {
  const gallery = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []),
    [images],
  );
  const [activeIdx, setActiveIdx] = useState(0);

  if (gallery.length === 0) return null;
  const activeSrc = gallery[activeIdx] || gallery[0];
  const showThumbs = gallery.length > 1;

  return (
    <div
      className="flex flex-col h-full"
      data-cms-scope={cmsScope}
      data-testid={`${testIdBase}-gallery`}
    >
      {/* Main image */}
      <div className="relative aspect-[4/3] md:flex-1 overflow-hidden">
        <img
          key={activeSrc}
          src={activeSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          data-cms-role="image"
          data-testid={`${testIdBase}-main-image`}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
        />
        {overlay}
      </div>

      {/* Thumbnails strip */}
      {showThumbs && (
        <div
          className="grid gap-2 p-2 bg-[#1A1513]/85"
          style={{ gridTemplateColumns: `repeat(${gallery.length}, minmax(0, 1fr))` }}
          data-testid={`${testIdBase}-thumbs`}
        >
          {gallery.map((src, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={`${testIdBase}-thumb-${i}`}
                type="button"
                onClick={() => setActiveIdx(i)}
                aria-label={`${alt} — photo ${i + 1}`}
                aria-pressed={isActive}
                data-testid={`${testIdBase}-thumb-${i}`}
                className={`relative aspect-[4/3] overflow-hidden group focus:outline-none transition-all duration-200 ${
                  isActive
                    ? `ring-2 ${accentClass} ring-offset-1 ring-offset-[#1A1513]`
                    : "opacity-70 hover:opacity-100"
                }`}
              >
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  data-cms-role="thumb"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CampImageGallery;
